import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const ContactPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate sending - in production, this would call an API
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "We'll get back to you within 24 hours."
      });
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-16">
      {/* Header */}
      <div className="text-center mb-12">
        <Mail size={64} className="mx-auto mb-6 text-gray-700" />
        <h1 className="text-5xl font-serif font-bold mb-4">
          Contact Us
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Have questions? Need help? Want to share feedback? We're here to listen.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        {/* Contact Info Cards */}
        <Card>
          <CardContent className="pt-6 text-center">
            <Mail className="mx-auto mb-4 text-blue-600" size={40} />
            <h3 className="font-bold mb-2">Email Us</h3>
            <a href="mailto:hello@centralcoasttimes.com" className="text-blue-600 hover:underline">
              hello@centralcoasttimes.com
            </a>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 text-center">
            <MapPin className="mx-auto mb-4 text-green-600" size={40} />
            <h3 className="font-bold mb-2">Location</h3>
            <p className="text-gray-600">Central Coast, NSW<br />Australia</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 text-center">
            <Phone className="mx-auto mb-4 text-purple-600" size={40} />
            <h3 className="font-bold mb-2">Response Time</h3>
            <p className="text-gray-600">Within 24 hours<br />7 days a week</p>
          </CardContent>
        </Card>
      </div>

      {/* Contact Form */}
      <Card>
        <CardHeader>
          <CardTitle>Send Us a Message</CardTitle>
          <CardDescription>
            Fill out the form below and we'll get back to you as soon as possible
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Your name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="your.email@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                placeholder="What's this about?"
                required
              />
            </div>

            <div>
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                placeholder="Tell us more..."
                rows={8}
                required
              />
            </div>

            <Button type="submit" size="lg" className="w-full" disabled={loading}>
              {loading ? (
                'Sending...'
              ) : (
                <>
                  <Send className="mr-2" size={16} />
                  Send Message
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* FAQ Section */}
      <div className="mt-16">
        <h2 className="text-3xl font-serif font-bold mb-8 text-center">Frequently Asked Questions</h2>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">How quickly can I publish a counter-article?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                Our AI generates a draft in about 60 seconds. You can review, edit, and publish within 2-5 minutes total.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Do I need writing skills?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                No! Our AI handles all the writing. You just provide the facts and your side of the story. The AI turns it into professional journalism.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Will my article appear on Google?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                Yes. Google indexes our articles within 24-48 hours. Your counter-narrative will appear in search results alongside the original story.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Is there a cost to use this service?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                The platform is free to use. We believe everyone deserves the right to share their side of the story.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Can I upload photos and evidence?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                Yes! Every submission form includes photo upload. You can upload up to 5 images including photos, screenshots, documents, or other evidence to support your story.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">What if I have legal concerns?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                We recommend sticking to facts and truth. If you have evidence of defamation or false reporting, consider consulting with a lawyer. Our platform helps you share your side, but we don't provide legal advice.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
